import { FC, JSX, ReactElement, useContext, useEffect, useMemo, useRef, useState } from "react";
import { ZipProjectContext } from "./ZipProjectContext";
import { QESButton, QESSelect } from "@app/shared/qes-standards";
import { formatDateTime, TooltipWrapper } from "@app/shared";
import { Bar, BarChart, CartesianGrid, Legend, Rectangle, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { ProjectPerspective, ProjectRunConfigurationType } from "@app/projects/enumerators";
import { ZipClassTrendChartItem, ZipCodeTrendChartItem } from "./interfaces/zipTrendChartItems";
import { TrendChartItemType } from "@app/velvet/enumerators";
import { Flyout } from "@app/shared/Flyouts";
import FlyoutContextProvider, { FlyoutContext } from "@app/shared/Flyouts/FlyoutContext";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { RoleRestricted, Roles } from "@app/user";
import { ZipProjectUpdateTimesliceCommentFlyout } from "./ZipProjectUpdateTimesliceCommentFlyout";
import ProjectRunConfiguration from "@app/projects/projectRunConfiguration";

const barWidth = 24;
const barColor = "#cc3a52"; // $qes-primary-red

interface TooltipData {
  dataPrimary: string;
  dataSecondary: string;
  fill: string;
  hasNoData: boolean;
  isDualMembership: boolean;
  label: string;
  name: string;
  primaryMembershipName: string;
  publishComment: string;
  runConfigurationId: number;
  secondaryMembershipName: string;
  showEditButton: boolean;
  value: string;
}

interface ZipProjectTrendChartProps {
  runConfigurationOptions: JSX.Element[];
  compareToRunConfigurationOptions: JSX.Element[];
}

const WrappedZipProjectTrendChart: FC<ZipProjectTrendChartProps> = (props: ZipProjectTrendChartProps) => {
  const projectContext = useContext(ZipProjectContext);
  const { state } = projectContext;
  const [hoveredBar, setHoveredBar] = useState(null);
  const [isCursorInTooltip, setIsCursorInTooltip] = useState(false);
  const [frozenTooltipData, setFrozenTooltipData] = useState<TooltipData>();
  const [mouseX, setMouseX] = useState();
  const chartRef = useRef<HTMLDivElement>(null);
  const primaryTimesliceRef = useRef<HTMLSelectElement>(null);
  const flyoutContext = useContext(FlyoutContext);

  useEffect(() => {
    // should trigger on any flyout close, not just success
    if (!flyoutContext.isFlyoutVisible && !isCursorInTooltip) {
      setFrozenTooltipData(undefined);
      setMouseX(undefined);
      setIsCursorInTooltip(false);
    }
  }, [flyoutContext.isFlyoutVisible, isCursorInTooltip]);

  useEffect(() => {
    // Reset the frozenTooltipData whenever the tooltip disappears, because it's possible for it to disappear without the mouse leaving it
    const chartObserver = new MutationObserver((e) => {
      const target = e[0].target as HTMLElement;
      if (target.classList.contains("recharts-tooltip-wrapper") && (target.style.visibility === "hidden" || target.children.length === 0)) {
        setFrozenTooltipData(undefined);
        setMouseX(undefined);
        setIsCursorInTooltip(false);
      }
    });
    chartObserver.observe(chartRef.current, { childList: true, subtree: true });

    return () => {
      chartObserver.disconnect();
    }
  }, []);

  const chartData = useMemo(() => {
    // we want to show a maximum of 12 items, but want them to be sized consistently regardless
    // so we'll pad the start of the array until it's 12 items
    const trendChartItemCount = 12;
    // we receive the trend chart data with most recent first and going backwards
    // but we render left to right, so we need oldest first
    const paddedTrendChartData: (ZipCodeTrendChartItem | ZipClassTrendChartItem)[] = [...state.trendChartData];
    const missingTimeslices = state.project.projectRunConfigurations
      .filter((prc) => !paddedTrendChartData.find((p) => p.projectRunConfigurationId === prc.projectRunConfigurationId) && prc.projectRunConfigurationType !== ProjectRunConfigurationType.DualMembershipSupplemental);

    missingTimeslices.forEach((prc) => {
      paddedTrendChartData.push({
        dateCreatedOrProjected: prc.dateCreated.toString(),
        dualMembershipRunConfigurationId: undefined,
        gapCount: 0,
        gapCountSecondary: undefined,
        hasNoData: true,
        itemType: TrendChartItemType.OlderPublished,
        metCount: 0,
        metCountSecondary: undefined,
        notMetCount: 0,
        notMetCountSecondary: undefined,
        projectRunConfigurationId: prc.projectRunConfigurationId,
      })
    })
    paddedTrendChartData.sort((a, b) => new Date(a.dateCreatedOrProjected) > new Date(b.dateCreatedOrProjected) ? 1 : -1);

    while (paddedTrendChartData.length < trendChartItemCount) {
      paddedTrendChartData.unshift({
        dateCreatedOrProjected: null,
        dualMembershipRunConfigurationId: null,
        gapCount: 0,
        gapCountSecondary: 0,
        itemType: null,
        metCount: 0,
        metCountSecondary: 0,
        notMetCount: 0,
        notMetCountSecondary: 0,
        projectRunConfigurationId: null,
      });
    }

    return paddedTrendChartData;
  }, [state.trendChartData]);

  const barDataKeyPrimary = useMemo(() => {
    if (state.runConfiguration.projectRunConfigurationType === ProjectRunConfigurationType.DualMembershipPrimary
      || state.runConfiguration.projectRunConfigurationType === ProjectRunConfigurationType.Standard) {
      if (state.perspective === ProjectPerspective.ByZipClass) {
        return "gapCount";
      } else if (state.perspective === ProjectPerspective.ByZipCode) {
        return "notMetCount";
      }
    } else if (state.runConfiguration.projectRunConfigurationType === ProjectRunConfigurationType.DualMembershipSupplemental) {
      if (state.perspective === ProjectPerspective.ByZipClass) {
        return "gapCountSecondary";
      } else if (state.perspective === ProjectPerspective.ByZipCode) {
        return "notMetCountSecondary";
      }
    }
  }, [state.perspective, state.runConfiguration]);

  const barDataKeySecondary = useMemo(() => {
    if (state.isDualMembershipMode) {
      if (state.perspective === ProjectPerspective.ByZipClass) {
        return "gapCountSecondary";
      } else if (state.perspective === ProjectPerspective.ByZipCode) {
        return "notMetCountSecondary";
      }
    }
    return null;
  }, [state.perspective, state.isDualMembershipMode]);

  const dataMax = useMemo(() => {
    if (!state.project || !state.trendChartData)
      return "auto";
    let values = chartData.flatMap((d) => [(d as ZipCodeTrendChartItem & ZipClassTrendChartItem)[barDataKeyPrimary], (d as ZipCodeTrendChartItem & ZipClassTrendChartItem)[barDataKeySecondary]]).filter((n) => !!n);
    // if the y axis has no range (e.g. [0, 0]), the x axis floats in the middle of the chart
    // Math.max(...[]) returns -Infinity, and [0, -Infinity] behaves the same as [0, 0] here
    if (values.length === 0) {
      values = [1];
    }
    // round up to the nearest multiple of 5 above the highest value in the chart
    return Math.ceil(Math.max(...values) / 5) * 5;
  }, [state.project, state.trendChartData]);

  const handleBarClick = (runConfigurationId: number) => {
    if (!runConfigurationId
      || runConfigurationId === state.runConfiguration.projectRunConfigurationId
      || state.isCompareMode && runConfigurationId === state.compareToRunConfiguration.projectRunConfigurationId
    )
      return;

    if (state.isCompareMode) {
      projectContext.onChangeCompareToRunConfiguration(runConfigurationId);
    } else {
      projectContext.onChangeRunConfiguration(runConfigurationId);
    }
  }

  return (
    <div className="zip-project-overview__trend-chart-card card">
      <div className="col-3 zip-project-overview__timeslice-select-block mr-2">
        <h3 className="label-s">Trend Chart</h3>
        <h4 className="label-s font-weight-normal">Data Selection</h4>
        <p>Select a project time-slice or hover over the chart to view historical data.</p>
        <div className="d-flex flex-column justify-content-start flex-fill">
          <QESSelect
            label="Results as of:"
            testId="zip-project-timeslice-management__time-slice--trend-chart"
            value={state.runConfiguration?.projectRunConfigurationId}
            onChange={(e) => projectContext.onChangeRunConfiguration(parseInt(e.target.value, 10))}
            disabled={state.loading}
            qesRef={primaryTimesliceRef}
            containerClassName="mb-3"
          >
            {props.runConfigurationOptions}
          </QESSelect>
          {props.compareToRunConfigurationOptions.length > 1 && (
            <TooltipWrapper text={state.isDualMembershipMode ? "Time slice selection is disabled when comparing memberships" : ""} triggerWidth="100%">
              <QESSelect
                label="Compare Results to:"
                testId="zip-project-timeslice-management__compared-time-slice--trend-chart"
                value={state.compareToRunConfiguration?.projectRunConfigurationId || ""}
                onChange={(e) => projectContext.onChangeCompareToRunConfiguration(parseInt(e.target.value, 10))}
                disabled={state.loading || state.isDualMembershipMode}
                allowEmpty={true}
                placeholder="None"
              >
                {props.compareToRunConfigurationOptions}
              </QESSelect>
            </TooltipWrapper>
          )}
          {/* When run frequency is added, it goes here */}
        </div>
      </div>
      <div className="col-9 zip-project-overview__trend-chart-container pl-3">
        <ResponsiveContainer height="100%" width="100%" ref={chartRef}>
          <BarChart
            data={chartData}
            margin={{ bottom: 120, left: chartData[0].dateCreatedOrProjected ? 40 : 0, top: 18 }}
          >
            <CartesianGrid vertical={false} pointerEvents="none" />
            {/* interval 0 forces it to show every tick label */}
            <XAxis dataKey="dateCreatedOrProjected" axisLine={false} tickLine={false} interval={0} tick={<CustomXAxisTick />} />
            <YAxis axisLine={false} tickLine={false} allowDecimals={false} orientation="right" domain={[0, dataMax]} />
            <Legend align="left" verticalAlign="top" content={<CustomLegend fill={barColor} />} />
            {/* A11Y NOTE - This tooltip works on mouse hover, but not on focus. 
              This may provide some insight: https://github.com/recharts/recharts/issues/1231#issuecomment-801143632 */}
            {/* The poorly-named cursor prop is the background shade of a bar when hovered. */}
            <Tooltip
              content={
                <CustomTooltip
                  setIsCursorInTooltip={setIsCursorInTooltip}
                  setFrozenTooltipData={setFrozenTooltipData}
                  isDualMembership={state.isDualMembershipMode}
                  frozenTooltipData={frozenTooltipData}
                  handleOpenFlyout={flyoutContext.handleOpen}
                  setMouseX={setMouseX}
                  chartRef={chartRef}
                  runConfigurations={state.project.projectRunConfigurations}
                  isFlyoutVisible={flyoutContext.isFlyoutVisible}
                  primaryKey={barDataKeyPrimary}
                  secondaryKey={barDataKeySecondary}
                />
              }
              cursor={false}
              wrapperStyle={{ pointerEvents: "auto" }}
              offset={8}
              position={{ x: isCursorInTooltip ? mouseX : undefined, y: 80 }}
            />
            {/* A11Y NOTE - These bars (and the whole chart) are completely ignored in keyboard navigation. TabIndex does work but the tooltips need to be updated. */}
            <Bar
              dataKey={barDataKeyPrimary}
              name="Adequacy Gaps"
              fill={barColor}
              barSize={barWidth}
              background={
                <CustomBarBackground
                  activeRunConfiguration={state?.runConfiguration.projectRunConfigurationId}
                  compareToRunConfiguration={state?.compareToRunConfiguration?.projectRunConfigurationId}
                  runConfigurationType={state.runConfiguration.projectRunConfigurationType}
                  hoveredBar={hoveredBar}
                  setHoveredBar={setHoveredBar}
                  handleClick={handleBarClick}
                  isDualMembership={state.isDualMembershipMode}
                />
              }
              shape={<CustomBarShape isSecondary={state.runConfiguration.projectRunConfigurationType === ProjectRunConfigurationType.DualMembershipSupplemental} isDualMembership={state.isDualMembershipMode} />}
              pointerEvents="none"
            />
            {state.isDualMembershipMode && (
              <Bar
                dataKey={barDataKeySecondary}
                name="Adequacy Gaps"
                fill={barColor}
                barSize={barWidth}
                background={false}
                pointerEvents="none"
                shape={<CustomBarShape isSecondary={true} isDualMembership={true} />}
              />
            )}
          </BarChart>
        </ResponsiveContainer>
      </div>
      <RoleRestricted allowedRoles={[Roles.CLIENT_ADMIN, Roles.RESOURCE_ADMIN]}>
        <Flyout
          content={{
            body: (
              <ZipProjectUpdateTimesliceCommentFlyout
                existingComment={frozenTooltipData?.publishComment || ""}
                runConfigurationId={frozenTooltipData?.runConfigurationId}
                onSubmit={projectContext.onEditTimeslicePublishComment}
              />),
            title: "Publish Options",
          }}
          size="40"
          // The tooltip won't exist when the flyout closes, so we'll redirect focus to the 
          // timeslice dropdown instead of the first element in the app view (the breadcrumbs).
          // The bars in the trend chart are not focusable at this time.
          triggerElement={primaryTimesliceRef}
        />
      </RoleRestricted>
    </div>
  );
}

/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
const CustomXAxisTick = (props: any) => {
  // recharts makes this difficult, but the typing is at least  { x: number, y: number, payload: { value: string }}
  const { x, y, payload } = props;

  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={0} dx={-2} dy={8} textAnchor="end" fill="#666" transform="rotate(-35)">
        {!!payload.value && formatDateTime(payload.value)}
      </text>
    </g>
  );
}

/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
const CustomLegend = (props: any) => {
  // this is the default legend, except we only want to render one instance of Adequacy Gaps in dual membership mode
  // by default it does one per bar
  return (
    <>
      <i className="qes-dashboard__color-rect" style={{ backgroundColor: props.fill }} /> {props.payload[0].value}
    </>
  )
}

/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
const CustomTooltip = (props: any) => {
  // active is a boolean for being currently hovered
  // payload is an array of color, dataKey, fill, hide, name, value, and others
  // label is the x axis tick, or the same as payload[i].payload.dateCreatedorProjected
  const { active, payload, label, isDualMembership } = props;

  // "filler" spaces have no label and shouldn't show a tooltip
  if (!!active && payload?.length && !!label) {
    const thisRunConfiguration: ProjectRunConfiguration = props.runConfigurations.find((rc: ProjectRunConfiguration) => rc.projectRunConfigurationId === props.payload[0].payload.projectRunConfigurationId);
    const companionRunConfiguration: ProjectRunConfiguration = props.runConfigurations.find((rc: ProjectRunConfiguration) => rc.projectRunConfigurationId === thisRunConfiguration?.companionProjectRunConfigurationId);
    const thisTooltipData: TooltipData = !!props.frozenTooltipData ? props.frozenTooltipData : {
      dataPrimary: payload[0].payload[props.primaryKey],
      dataSecondary: payload[0].payload[props.secondaryKey],
      fill: payload[0].fill,
      hasNoData: payload[0].payload.hasNoData,
      isDualMembership,
      label: formatDateTime(label),
      name: payload[0].name,
      primaryMembershipName: thisRunConfiguration?.memberSetAlias || thisRunConfiguration?.memberSetName,
      publishComment: payload[0].payload.publishComment,
      runConfigurationId: payload[0].payload.projectRunConfigurationId,
      secondaryMembershipName: companionRunConfiguration?.memberSetAlias || companionRunConfiguration?.memberSetName,
      showEditButton: !thisRunConfiguration?.isProjected
        && !thisRunConfiguration?.isTermed
        && !thisRunConfiguration?.isProjectedTermed
        && !!thisRunConfiguration?.datePublished,
      value: payload[0].value.toLocaleString(),
    };

    const calculateXPos = (e: React.MouseEvent) => {
      const tooltipBox = (e.target as HTMLElement).getBoundingClientRect();
      const chartBox = props.chartRef.current.getBoundingClientRect();
      props.setMouseX(tooltipBox.x - chartBox.x);
    }

    return (
      <div
        onMouseEnter={(e) => { calculateXPos(e); props.setIsCursorInTooltip(true); props.setFrozenTooltipData(thisTooltipData); }}
        onMouseLeave={() => {
          props.setIsCursorInTooltip(false);
          if (!props.isFlyoutVisible)
            props.setFrozenTooltipData(undefined);
        }}
      >
        <div className="qes-dashboard__custom-tooltip zip-project-timeslice-management__tooltip">
          <RoleRestricted allowedRoles={[Roles.CLIENT_ADMIN, Roles.RESOURCE_ADMIN]}>
            {thisTooltipData.showEditButton
              && (
                <QESButton
                  qesType="secondary"
                  onClick={props.handleOpenFlyout}
                  qesTestId="zip-project-timeslice-management__flyout-link"
                  className="zip-project-timeslice-management__change-publish-comment-button"
                >
                  <QesIcon iconName={QesIcons.FontAwesome.Edit} description="click to edit the timeslice publish comment" />
                </QESButton>
              )}
          </RoleRestricted>
          <p className="qes-dashboard__tooltip-title zip-project-timeslice-management__tooltip-title">{thisTooltipData.publishComment || thisTooltipData.label}</p>
          {thisTooltipData.hasNoData ? <>No Data</> : (
            <div>
              <i className="qes-dashboard__color-rect" style={{ backgroundColor: thisTooltipData.fill }} />
              {!thisTooltipData.isDualMembership ? (
                <>
                  <span>{thisTooltipData.name}:</span> {thisTooltipData.value}
                </>
              ) : (
                <>
                  <span>{thisTooltipData.name}:</span>
                  <div className="d-flex justify-content-between"><span>{thisTooltipData.primaryMembershipName}:</span> <span>{thisTooltipData.dataPrimary}</span></div>
                  {!!thisTooltipData.secondaryMembershipName && <div className="d-flex justify-content-between"><span>{thisTooltipData.secondaryMembershipName}:</span> <span>{thisTooltipData.dataSecondary}</span></div>}
                </>
              )}
            </div>
          )}

        </div>
      </div >
    )
  }
}

/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
const CustomBarBackground = (props: any) => {
  const isActive = props.payload.projectRunConfigurationId === props.activeRunConfiguration
    || (!props.isDualMembership
      && props.runConfigurationType === ProjectRunConfigurationType.DualMembershipSupplemental
      && props.payload.dualMembershipRunConfigurationId === props.activeRunConfiguration);
  const isHovered = !!props.hoveredBar && props.payload.projectRunConfigurationId === props.hoveredBar;
  const isCompared = props.payload.projectRunConfigurationId === props.compareToRunConfiguration;

  let strokeWidth = 1;
  if ((!!props.compareToRunConfiguration && isCompared) || (!props.compareToRunConfiguration && isActive)) {
    strokeWidth = 2;
  }

  return (
    <Rectangle
      className={props.className}
      cursor={!!props.payload.projectRunConfigurationId && !isActive && !isCompared ? "pointer" : "default"}
      // $qes-primary-blue
      stroke={isActive || isHovered || isCompared ? "#056edf" : "transparent"}
      strokeWidth={strokeWidth}
      fill="transparent"
      width={props.isDualMembership ? props.width * 3.2 : props.width * 2}
      strokeDasharray={isHovered && !isActive && !isCompared ? 6 : undefined}
      // centers the outline around the bar
      x={props.x - (props.width / 2)}
      y={props.y}
      height={props.height - 1}
      onMouseEnter={() => { props.setHoveredBar(props.payload.projectRunConfigurationId); }}
      onMouseLeave={() => props.setHoveredBar(null)}
      onClick={() => !isActive ? props.handleClick(props.payload.projectRunConfigurationId) : false}
    />
  );
}

/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
const CustomBarShape = (props: any) => {
  const hasNoData = props.payload?.hasNoData;
  return (
    <>
      <Rectangle
        pointerEvents="none"
        // #f2f2fc is $qes-primary-gray-3
        fill={hasNoData ? "#f2f2fc" : props.fill}
        height={hasNoData ? 200 : props.height}
        width={props.width}
        x={props.x}
        y={hasNoData ? props.y - 200 : props.y}
      />
      {props.itemType === TrendChartItemType.Unpublished && (!props.isDualMembership || (props.isDualMembership && props.isSecondary)) && (
        <text
          transform={`rotate(270, ${props.x + props.width - 8}, ${props.y + props.height})`}
          className="zip-project-overview__not-yet-published"
          x={props.x + props.width}
          y={props.isDualMembership ? props.y + props.height - 12 : props.y + props.height + 3}
          filter="url(#drop-shadow)"
        >
          Not Yet Published
        </text>
      )}
    </>
  )
}

const ZipProjectTrendChart: FC<ZipProjectTrendChartProps> = (props: ZipProjectTrendChartProps): ReactElement => (
  <FlyoutContextProvider>
    <WrappedZipProjectTrendChart {...props} />
  </FlyoutContextProvider>
);

export { ZipProjectTrendChart };